// src/contexts/DocumentSigning.js
import React, { useEffect, useState, useRef } from 'react';
import { 
    Modal, 
    Button, 
    message, 
    Typography, 
    Card, 
    Alert, 
    Progress, 
    Spin,
    Space,
    Divider,
    Badge,
    Steps,
    Collapse,
    Row,
    Col,
    Tag
} from 'antd';
import { 
    FileTextOutlined, 
    CheckCircleOutlined, 
    FileOutlined,
    DownloadOutlined,
    ReloadOutlined,
    EditOutlined,
    RightOutlined,
    LeftOutlined,
    CheckOutlined
} from '@ant-design/icons';
import Lottie from 'lottie-react';
import successAnimation from '../components/lottie/success.json';
import { useAuth } from './AuthContext';
import { db, storage } from '../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, getDocs, doc, updateDoc, Timestamp, setDoc, getDoc } from 'firebase/firestore';
import { regeneratePDFWithSignature } from '../components/utils/regeneratePDFWithSignature';
import DocumentGenerationAnimation from '../components/DocumentGenerationAnimation';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { Panel } = Collapse;

const DocumentSigning = ({ visible, onClose }) => {
    const { currentUser } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [isSignatureModalVisible, setIsSignatureModalVisible] = useState(false);
    const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [allDocumentsSigned, setAllDocumentsSigned] = useState(false);
    const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
    const [isPortrait, setIsPortrait] = useState(window.matchMedia("(orientation: portrait)").matches);
    const [currentContractId, setCurrentContractId] = useState(null);
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [emptySignatureData, setEmptySignatureData] = useState('');
    const [hasDrawn, setHasDrawn] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSigning, setIsSigning] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [isGeneratingDocuments, setIsGeneratingDocuments] = useState(false);
    const [generationCompleted, setGenerationCompleted] = useState(false);
    
    // Новое состояние для пошагового интерфейса
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const [unsignedDocuments, setUnsignedDocuments] = useState([]);
    const [signedDocuments, setSignedDocuments] = useState([]);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [showContent, setShowContent] = useState(true);

    // Lista dokumentów do wygenerowania
    const documentsToGenerate = [
        'Umowa o współpracy',
        'Regulamin wewnętrzny',
        'Oświadczenie RODO',
        'Umowa najmu pojazdu',
        'Protokół przekazania'
    ];

    // FUNKCJA GENEROWANIA DOKUMENTÓW
    const generateRealDocuments = async () => {
        console.log('🚀 Generowanie dokumentów...');
        setShowContent(false);
        
        // Małe opóźnienie dla płynnego przejścia
        await new Promise(resolve => setTimeout(resolve, 300));
        
        setIsGeneratingDocuments(true);
        setGenerationCompleted(false);
        
        try {
            // Importuj funkcję generowania
            const { generateDocumentsForUser } = await import('../components/utils/documentGenerator');
            
            // Sprawdź czy dokumenty już istnieją
            const existingDocs = await getDocs(collection(db, `users/${currentUser.uid}/umowy`));
            
            if (!existingDocs.empty) {
                console.log('✅ Dokumenty już istnieją');
                messageApi.info('Dokumenty już zostały wygenerowane');
                setIsGeneratingDocuments(false);
                setShowContent(true);
                return;
            }
            
            // Generuj dokumenty
            const success = await generateDocumentsForUser(currentUser.uid);
            
            if (success) {
                // Pokaż animację sukcesu
                setGenerationCompleted(true);
                
                // Odśwież listę po 2 sekundach
                setTimeout(async () => {
                    await fetchDocuments();
                    setIsGeneratingDocuments(false);
                    setGenerationCompleted(false);
                    setShowContent(true);
                }, 2000);
            } else {
                throw new Error('Generowanie nie powiodło się');
            }
            
        } catch (error) {
            console.error('❌ Błąd generowania dokumentów:', error);
            messageApi.error(`Błąd: ${error.message}`);
            setIsGeneratingDocuments(false);
            setGenerationCompleted(false);
            setShowContent(true);
        }
    };

    // POBIERANIE DOKUMENTÓW
    const fetchDocuments = async () => {
        if (currentUser) {
            console.log('📄 Pobieranie dokumentów dla użytkownika:', currentUser.uid);
            setIsLoading(true);
            
            try {
                const docsSnapshot = await getDocs(collection(db, `users/${currentUser.uid}/umowy`));
                console.log('📄 Znaleziono dokumentów:', docsSnapshot.size);
                
                if (docsSnapshot.empty) {
                    console.log('⚠️ Brak dokumentów - rozpoczynam generowanie');
                    setDocuments([]);
                    setIsLoading(false);
                    
                    // Automatycznie generuj dokumenty
                    if (visible) {
                        await generateRealDocuments();
                    }
                    return;
                }
                
                const docs = docsSnapshot.docs.map(docSnapshot => {
                    const data = docSnapshot.data();
                    return { id: docSnapshot.id, ...data };
                });
                
                setDocuments(docs);
                
                // Rozdziel dokumenty na podpisane i niepodpisane
                const unsigned = docs.filter(d => !d.signed);
                const signed = docs.filter(d => d.signed);
                
                setUnsignedDocuments(unsigned);
                setSignedDocuments(signed);
                
                // Sprawdź i skoryguj currentDocumentIndex
                if (currentDocumentIndex >= unsigned.length && unsigned.length > 0) {
                    // Jeśli indeks jest poza zakresem, ustaw na 0
                    setCurrentDocumentIndex(0);
                } else if (unsigned.length === 0) {
                    // Jeśli nie ma już niepodpisanych dokumentów, zresetuj do 0
                    setCurrentDocumentIndex(0);
                }
                
                const allSigned = unsigned.length === 0;
                setAllDocumentsSigned(allSigned);
                
                // Jeśli wszystkie podpisane, pokaż modal sukcesu
                if (allSigned && docs.length > 0) {
                    setIsSuccessModalVisible(true);
                }
                
            } catch (error) {
                console.error('❌ Błąd podczas pobierania dokumentów:', error);
                messageApi.error('Błąd podczas pobierania dokumentów');
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (visible && currentUser) {
            fetchDocuments();
        }
    }, [currentUser, visible]);

    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight);
            // Реинициализируем canvas при изменении размера окна если модальное окно открыто
            if (isSignatureModalVisible && !isPortrait) {
                setTimeout(initializeCanvas, 100);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isSignatureModalVisible, isPortrait]);

    useEffect(() => {
        const handleOrientationChange = (e) => {
            setIsPortrait(e.matches);
            if (!e.matches && isSignatureModalVisible) {
                setTimeout(initializeCanvas, 200);
            }
        };

        const portraitMediaQuery = window.matchMedia("(orientation: portrait)");
        portraitMediaQuery.addEventListener('change', handleOrientationChange);

        return () => {
            portraitMediaQuery.removeEventListener('change', handleOrientationChange);
        };
    }, [isSignatureModalVisible]);

    // Дополнительный useEffect для отслеживания видимости модального окна подписи
    useEffect(() => {
        if (isSignatureModalVisible && !isPortrait) {
            const timer = setTimeout(() => {
                initializeCanvas();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isSignatureModalVisible, isPortrait]);

    const initializeCanvas = () => {
        const canvas = canvasRef.current;
        if (canvas) {
            // Получаем реальные размеры canvas элемента
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;
            
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.strokeStyle = "black";
                ctx.lineWidth = window.innerHeight < 400 ? 2 : 3;
                ctx.lineCap = "round";
                ctx.lineJoin = "round";
                ctxRef.current = ctx;
                setEmptySignatureData(canvas.toDataURL());
            }
        }
    };

    const getCanvasCoordinates = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        if (event.touches && event.touches.length > 0) {
            return {
                x: (event.touches[0].clientX - rect.left) * scaleX,
                y: (event.touches[0].clientY - rect.top) * scaleY,
            };
        } else {
            return {
                x: (event.clientX - rect.left) * scaleX,
                y: (event.clientY - rect.top) * scaleY,
            };
        }
    };

    const startDrawing = (e) => {
        e.preventDefault(); 
        e.stopPropagation();
        document.body.classList.add('disable-scroll');
    
        const ctx = ctxRef.current;
        if (ctx) {
            setDrawing(true);
            const { x, y } = getCanvasCoordinates(e);
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x, y);
            ctx.stroke();
        }
    };
    
    const draw = (e) => {
        e.preventDefault(); 
        e.stopPropagation();
    
        if (!drawing) return;
        const ctx = ctxRef.current;
        if (ctx) {
            const { x, y } = getCanvasCoordinates(e);
            ctx.lineTo(x, y);
            ctx.stroke();
            setHasDrawn(true);
        }
    };

    const stopDrawing = () => {
        setDrawing(false);
        document.body.classList.remove('disable-scroll');
    };

    const clearSignature = () => {
        const ctx = ctxRef.current;
        if (ctx) {
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            setHasDrawn(false);
        }
    };

    // FUNKCJA ZAPISYWANIA PODPISU Z REGENERACJĄ PDF
    const saveSignature = async () => {
        if (!canvasRef.current) return;
        
        const signatureData = canvasRef.current.toDataURL();
        if (signatureData === emptySignatureData) {
            messageApi.error("Proszę najpierw złożyć podpis.");
            return;
        }

        setIsSigning(true);
        const loadingMessage = messageApi.loading('Zapisywanie podpisu...', 0);

        try {
            // Konwertuj canvas na blob
            const byteCharacters = atob(signatureData.split(',')[1]);
            const byteArrays = [];
            
            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                const slice = byteCharacters.slice(offset, offset + 512);
                const byteNumbers = Array.from(slice, char => char.charCodeAt(0));
                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }

            const blob = new Blob(byteArrays, { type: "image/png" });
            
            // Upload podpisu do Storage
            const signatureRef = ref(storage, `signatures/${currentUser.uid}/${currentContractId}_${Date.now()}.png`);
            console.log('📤 Uploading signature...');
            
            await uploadBytesResumable(signatureRef, blob);
            const signatureURL = await getDownloadURL(signatureRef);
            console.log('✅ Signature uploaded:', signatureURL);
            
            const signedAt = Timestamp.now();

            // Zaktualizuj dokument jako podpisany
            await updateDoc(doc(db, `users/${currentUser.uid}/umowy`, currentContractId), {
                signature: signatureURL,
                signed: true,
                signedAt: signedAt,
                status: 'Dokument podpisany',
                signatureType: 'canvas',
            });

            console.log('✅ Document marked as signed');
            loadingMessage();
            
            // Regeneruj PDF z podpisem
            try {
                const regeneratingMsg = messageApi.loading('Generowanie PDF z podpisem...', 0);
                console.log('🔄 Regenerating PDF with signature...');
                
                const newPdfUrl = await regeneratePDFWithSignature(
                    currentUser.uid, 
                    currentContractId, 
                    signatureURL
                );
                
                console.log('✅ PDF regenerated successfully:', newPdfUrl);
                regeneratingMsg();
                messageApi.success("Dokument został pomyślnie podpisany i zaktualizowany!");
                
            } catch (pdfError) {
                console.error('❌ Błąd regenerowania PDF:', pdfError);
                messageApi.warning("Dokument został podpisany, ale PDF nie został zaktualizowany. Dokument jest nadal ważny.");
                
                // Opcjonalnie: zapisz błąd do dokumentu
                await updateDoc(doc(db, `users/${currentUser.uid}/umowy`, currentContractId), {
                    pdfRegenerationError: pdfError.message,
                    pdfRegenerationAttempt: Timestamp.now()
                }).catch(console.error);
            }

            // Wyczyść canvas i zamknij modal
            clearSignature();
            setHasDrawn(false);
            setIsSignatureModalVisible(false);

            // Odśwież listę dokumentów
            await fetchDocuments();
            
            // Po odświeżeniu listy, zresetuj indeks do 0 lub zachowaj prawidłowy
            // Nie zwiększaj indeksu tutaj - zostanie to obsłużone przez fetchDocuments
            
            // Sprawdź czy wszystkie dokumenty są podpisane
            const updatedDocs = await getDocs(collection(db, `users/${currentUser.uid}/umowy`));
            const allSigned = updatedDocs.docs.every(doc => doc.data().signed);
            
            if (allSigned) {
                console.log('🎉 Wszystkie dokumenty podpisane!');
                // Opcjonalnie: zmień status użytkownika
                await updateDoc(doc(db, 'users', currentUser.uid), {
                    documentsSignedAt: Timestamp.now(),
                    allDocumentsSigned: true
                }).catch(console.error);
            }

        } catch (error) {
            messageApi.destroy();
            messageApi.error("Błąd podczas zapisywania podpisu. Spróbuj ponownie.");
            console.error("❌ Error saving signature:", error);
        } finally {
            setIsSigning(false);
        }
    };

    const openSignatureModal = (docId) => {
        setCurrentContractId(docId);
        clearSignature();
        setHasDrawn(false);
        setIsSignatureModalVisible(true);
        // Увеличиваем задержку для правильной инициализации canvas с новыми размерами
        setTimeout(initializeCanvas, 200);
    };

    const openDocumentModal = (document) => {
        setSelectedDocument(document);
        setIsDocumentModalVisible(true);
    };

    const closeDocumentModal = () => {
        setIsDocumentModalVisible(false);
        setSelectedDocument(null);
    };

    // Nawigacja między dokumentami
    const goToPreviousDocument = () => {
        if (currentDocumentIndex > 0) {
            setCurrentDocumentIndex(currentDocumentIndex - 1);
        }
    };

    const goToNextDocument = () => {
        if (currentDocumentIndex < unsignedDocuments.length - 1) {
            setCurrentDocumentIndex(currentDocumentIndex + 1);
        }
    };

    // Blokuj zamknięcie jeśli nie wszystkie dokumenty są podpisane
    const handleClose = () => {
        if (documents.length > 0 && !allDocumentsSigned) {
            messageApi.warning('Musisz podpisać wszystkie dokumenty przed zamknięciem!');
            return;
        }
        onClose();
    };

    const getCurrentDocument = () => {
        if (unsignedDocuments.length === 0) return null;
        if (currentDocumentIndex >= unsignedDocuments.length) {
            // Zabezpieczenie przed nieprawidłowym indeksem
            setCurrentDocumentIndex(0);
            return unsignedDocuments[0];
        }
        return unsignedDocuments[currentDocumentIndex];
    };

    return (
        <>
            {contextHolder}
            
            <Modal
                open={visible}
                onCancel={handleClose}
                footer={null}
                maskClosable={false}
                closable={(allDocumentsSigned || documents.length === 0) && !isGeneratingDocuments && !generationCompleted}
                width="90%"
                style={{ 
                    maxWidth: '900px',
                    top: 10,
                    paddingBottom: 0
                }}
                styles={{
                    body: { 
                        maxHeight: 'calc(100vh - 100px)', 
                        overflowY: 'auto',
                        padding: '20px',
                        minHeight: '500px', // Stała minimalna wysokość
                        position: 'relative'
                    }
                }}
            >
                {/* Kontener z animacją generowania - na wierzchu */}
                {(isGeneratingDocuments || generationCompleted) && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'white',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px'
                    }}>
                        <DocumentGenerationAnimation
                            isGenerating={isGeneratingDocuments}
                            isCompleted={generationCompleted}
                            documents={documentsToGenerate}
                        />
                    </div>
                )}
                
                {/* Główna zawartość - zawsze renderowana ale ukryta podczas generowania */}
                <div style={{
                    opacity: showContent ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                    pointerEvents: (isGeneratingDocuments || generationCompleted) ? 'none' : 'auto'
                }}>
                    <Title level={3} style={{ marginBottom: 24, textAlign: 'center' }}>
                        <FileTextOutlined /> Dokumenty do podpisu
                    </Title>

                    <div id="documents-container">
                        {isLoading ? (
                            <div style={{ textAlign: 'center', padding: 40 }}>
                                <Spin size="large" />
                                <div style={{ marginTop: 16 }}>
                                    <Text>Ładowanie dokumentów...</Text>
                                </div>
                            </div>
                        ) : documents.length === 0 ? (
                            <Alert
                                message="Przygotowywanie dokumentów"
                                description="System automatycznie wygeneruje wszystkie wymagane dokumenty."
                                type="info"
                                showIcon
                            />
                        ) : windowHeight < 300 ? (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <Title level={4}>
                                📱 Zbyt mały ekran
                            </Title>
                            <Paragraph>
                                Proszę powiększyć okno lub użyć urządzenia z większym ekranem.
                            </Paragraph>
                        </div>
                    ) : (
                            <>
                                {/* Progress bar */}
                                <div style={{ marginBottom: 24 }}>
                                    <Progress 
                                        percent={Math.round((signedDocuments.length / documents.length) * 100)}
                                        status={allDocumentsSigned ? "success" : "active"}
                                        format={percent => `${signedDocuments.length} z ${documents.length} podpisanych`}
                                    />
                                </div>

                                {/* Dokumenty niepodpisane - pokazywane pojedynczo */}
                                {unsignedDocuments.length > 0 && (
                                    <>
                                        <Alert
                                            message={`Dokument ${currentDocumentIndex + 1} z ${unsignedDocuments.length} do podpisania`}
                                            type="info"
                                            showIcon
                                            style={{ marginBottom: 20 }}
                                        />
                                        
                                        {/* Aktualny dokument */}
                                        <Card
                                            title={
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                                                    <Space size="small">
                                                        <FileTextOutlined style={{ fontSize: '18px' }} />
                                                        <Text strong style={{ fontSize: '16px' }}>
                                                            {getCurrentDocument()?.documentType || getCurrentDocument()?.id || 'Dokument'}
                                                        </Text>
                                                    </Space>
                                                    <Tag color="red" style={{ margin: 0 }}>Niepodpisany</Tag>
                                                </div>
                                            }
                                            style={{ 
                                                marginBottom: 20,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                            }}
                                            bodyStyle={{ padding: '16px' }}
                                        >
                                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                                {getCurrentDocument()?.dataGeneracji && (
                                                    <Row>
                                                        <Col span={12}>
                                                            <Text type="secondary">Data generacji: </Text>
                                                            <Text strong>
                                                                {new Date(getCurrentDocument().dataGeneracji.seconds * 1000).toLocaleDateString('pl-PL')}
                                                            </Text>
                                                        </Col>
                                                        <Col span={12}>
                                                            {getCurrentDocument()?.dataWaznosci && (
                                                                <>
                                                                    <Text type="secondary">Ważny do: </Text>
                                                                    <Text strong>
                                                                        {new Date(getCurrentDocument().dataWaznosci.seconds * 1000).toLocaleDateString('pl-PL')}
                                                                    </Text>
                                                                </>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                )}
                                                
                                                <Divider style={{ margin: '12px 0' }} />
                                                
                                                <Row gutter={[12, 12]}>
                                                    <Col xs={24} sm={12}>
                                                        <Button 
                                                            onClick={() => openDocumentModal(getCurrentDocument())}
                                                            icon={<FileOutlined />}
                                                            block
                                                            style={{ height: 'auto', padding: '8px 16px' }}
                                                            disabled={!getCurrentDocument()}
                                                        >
                                                            Zobacz dokument
                                                        </Button>
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <Button 
                                                            type="primary" 
                                                            onClick={() => openSignatureModal(getCurrentDocument()?.id)}
                                                            icon={<EditOutlined />}
                                                            block
                                                            style={{ height: 'auto', padding: '8px 16px' }}
                                                            disabled={!getCurrentDocument()?.id}
                                                        >
                                                            Podpisz dokument
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </Space>
                                        </Card>

                                        {/* Nawigacja między dokumentami */}
                                        {unsignedDocuments.length > 1 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                                                <Button 
                                                    onClick={goToPreviousDocument}
                                                    disabled={currentDocumentIndex === 0}
                                                    icon={<LeftOutlined />}
                                                >
                                                    Poprzedni
                                                </Button>
                                                
                                                <Space>
                                                    {unsignedDocuments.map((_, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                backgroundColor: index === currentDocumentIndex ? '#1890ff' : '#d9d9d9',
                                                                transition: 'all 0.3s'
                                                            }}
                                                        />
                                                    ))}
                                                </Space>
                                                
                                                <Button 
                                                    onClick={goToNextDocument}
                                                    disabled={currentDocumentIndex === unsignedDocuments.length - 1}
                                                >
                                                    Następny
                                                    <RightOutlined />
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                                
                                {/* Podpisane dokumenty - w Collapse */}
                                {signedDocuments.length > 0 && (
                                    <>
                                        <Divider />
                                        <Collapse 
                                            ghost 
                                            expandIconPosition="end"
                                            defaultActiveKey={unsignedDocuments.length === 0 ? ['1'] : []}
                                        >
                                            <Panel 
                                                header={
                                                    <Space>
                                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                        <Text strong>Podpisane dokumenty ({signedDocuments.length})</Text>
                                                    </Space>
                                                }
                                                key="1"
                                            >
                                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                                    {signedDocuments.map(document => (
                                                        <Card
                                                            key={document.id}
                                                            size="small"
                                                            bodyStyle={{ padding: '12px' }}
                                                            style={{ 
                                                                backgroundColor: '#f6ffed', 
                                                                borderColor: '#b7eb8f'
                                                            }}
                                                        >
                                                            <Row align="middle" gutter={16}>
                                                                <Col flex="auto">
                                                                    <Space>
                                                                        <CheckOutlined style={{ color: '#52c41a' }} />
                                                                        <Text strong>{document.documentType || document.id}</Text>
                                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                            Podpisano: {
                                                                                document.signedAt 
                                                                                    ? new Date(document.signedAt.seconds * 1000).toLocaleDateString('pl-PL')
                                                                                    : 'Data nieznana'
                                                                            }
                                                                        </Text>
                                                                    </Space>
                                                                </Col>
                                                                <Col>
                                                                    <Button 
                                                                        onClick={() => openDocumentModal(document)}
                                                                        size="small"
                                                                        icon={<FileOutlined />}
                                                                        type="text"
                                                                    >
                                                                        Zobacz
                                                                    </Button>
                                                                </Col>
                                                            </Row>
                                                        </Card>
                                                    ))}
                                                </Space>
                                            </Panel>
                                        </Collapse>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    
                    <Divider />
                    
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type="primary"
                            onClick={handleClose}
                            disabled={!allDocumentsSigned && documents.length > 0}
                            size="large"
                        >
                            {allDocumentsSigned || documents.length === 0 
                                ? 'Zakończ' 
                                : `Podpisz wszystkie dokumenty (${unsignedDocuments.length} pozostało)`}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Modal podpisu */}
            <Modal
                open={isSignatureModalVisible}
                onCancel={() => !isSigning && setIsSignatureModalVisible(false)}
                footer={null}
                maskClosable={false}
                closable={!isSigning}
                width={isPortrait ? 600 : '95%'}
                title={
                    <Space>
                        <EditOutlined />
                        <Text>Podpisywanie dokumentu</Text>
                    </Space>
                }
                style={{ 
                    top: 10,
                    paddingBottom: 0,
                    maxWidth: isPortrait ? '600px' : '1000px'
                }}
                styles={{
                    body: {
                        height: `calc(${windowHeight}px - ${windowHeight < 350 ? 75 : windowHeight < 400 ? 85 : 95}px)`,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: windowHeight < 400 ? '8px' : windowHeight < 500 ? '12px' : '20px',
                        paddingBottom: '10px',
                        overflow: 'hidden'
                    }
                }}
            >
                {isPortrait ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <Title level={4}>
                            📱 Proszę obrócić urządzenie
                        </Title>
                        <Paragraph>
                            Obróć urządzenie w poziome położenie, aby podpisać dokument.
                        </Paragraph>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                        <Alert
                            message={windowHeight < 350 ? "Podpisz poniżej" : "Narysuj swój podpis poniżej"}
                            description={windowHeight >= 450 ? "Użyj myszki lub palca aby narysować podpis w polu poniżej" : null}
                            type="info"
                            showIcon={windowHeight >= 400}
                            style={{ marginBottom: windowHeight < 350 ? 4 : windowHeight < 400 ? 6 : windowHeight < 500 ? 8 : 12, flexShrink: 0 }}
                        />
                        
                        <div style={{ 
                            border: '2px dashed #d9d9d9', 
                            borderRadius: '8px',
                            padding: windowHeight < 350 ? '4px' : '8px',
                            backgroundColor: '#fafafa',
                            flex: 1,
                            display: 'flex',
                            minHeight: windowHeight < 350 ? '40px' : windowHeight < 400 ? '60px' : '100px',
                            position: 'relative',
                            width: '100%'
                        }}>
                            <canvas
                                ref={canvasRef}
                                style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    backgroundColor: '#fff', 
                                    borderRadius: '4px',
                                    cursor: 'crosshair', 
                                    touchAction: 'none',
                                    boxShadow: drawing ? 'inset 0 0 0 1px #1890ff' : 'none',
                                    transition: 'box-shadow 0.2s'
                                }}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                            {!hasDrawn && windowHeight >= 350 && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: '#bfbfbf',
                                    fontSize: windowHeight < 400 ? '12px' : '14px',
                                    pointerEvents: 'none',
                                    textAlign: 'center'
                                }}>
                                    <EditOutlined style={{ fontSize: windowHeight < 400 ? '20px' : '28px', display: 'block', margin: '0 auto 4px' }} />
                                    {windowHeight >= 400 && 'Narysuj tutaj podpis'}
                                </div>
                            )}
                        </div>
                        
                        <div style={{ marginTop: windowHeight < 400 ? 6 : windowHeight < 500 ? 8 : 12, flexShrink: 0 }}>
                            <Row gutter={[8, windowHeight < 350 ? 4 : 8]}>
                                <Col xs={24} sm={12}>
                                    <Button 
                                        onClick={clearSignature} 
                                        disabled={isSigning}
                                        block
                                        size={windowHeight < 350 ? "small" : windowHeight < 400 ? "small" : windowHeight < 500 ? "middle" : "large"}
                                    >
                                        {windowHeight < 350 ? "Wyczyść" : "Wyczyść podpis"}
                                    </Button>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Button 
                                        type="primary" 
                                        onClick={saveSignature} 
                                        disabled={!currentContractId}
                                        loading={isSigning}
                                        block
                                        size={windowHeight < 350 ? "small" : windowHeight < 400 ? "small" : windowHeight < 500 ? "middle" : "large"}
                                    >
                                        {windowHeight < 350 ? "Zapisz" : "Zapisz podpis"}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal dokumentu */}
            <Modal
                open={isDocumentModalVisible}
                onCancel={closeDocumentModal}
                footer={[
                    <Button key="close" onClick={closeDocumentModal}>
                        Zamknij
                    </Button>,
                    selectedDocument?.pdfUrl && (
                        <Button 
                            key="download" 
                            type="default"
                            icon={<DownloadOutlined />}
                            href={selectedDocument.pdfUrl}
                            target="_blank"
                        >
                            Pobierz PDF
                        </Button>
                    ),
                    !selectedDocument?.signed && (
                        <Button 
                            key="sign" 
                            type="primary" 
                            icon={<EditOutlined />}
                            onClick={() => {
                                closeDocumentModal();
                                openSignatureModal(selectedDocument.id);
                            }}
                        >
                            Podpisz
                        </Button>
                    )
                ]}
                width={900}
                title={
                    <Space>
                        <FileTextOutlined />
                        {selectedDocument?.documentType || selectedDocument?.id}
                    </Space>
                }
                style={{ 
                    top: 10,
                    paddingBottom: 0
                }}
                styles={{
                    body: {
                        maxHeight: 'calc(100vh - 120px)',
                        overflowY: 'auto'
                    }
                }}
            >
                {selectedDocument && (
                    <div>
                        <Space direction="vertical" style={{ width: '100%', marginBottom: 20 }}>
                            <div>
                                <Text type="secondary">Status: </Text>
                                {selectedDocument.signed ? (
                                    <Badge status="success" text={selectedDocument.status} />
                                ) : (
                                    <Badge status="error" text={selectedDocument.status} />
                                )}
                            </div>
                            {selectedDocument.signed && selectedDocument.signedAt && (
                                <div>
                                    <Text type="secondary">Data podpisu: </Text>
                                    <Text>{new Date(selectedDocument.signedAt.seconds * 1000).toLocaleDateString('pl-PL')}</Text>
                                </div>
                            )}
                            {selectedDocument.hasSignedVersion && (
                                <Alert
                                    message="Dokument zawiera Twój podpis"
                                    type="success"
                                    showIcon
                                    style={{ marginTop: 10 }}
                                />
                            )}
                        </Space>
                        
                        <iframe
                            src={selectedDocument.pdfUrl}
                            title="Dokument PDF"
                            style={{ 
                                width: '100%', 
                                height: windowHeight < 600 ? '400px' : '600px', 
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px'
                            }}
                        />
                    </div>
                )}
            </Modal>

            {/* Modal sukcesu */}
            <Modal
                open={isSuccessModalVisible}
                onCancel={() => {
                    setIsSuccessModalVisible(false);
                    onClose();
                }}
                footer={null}
                maskClosable={false}
                width={500}
                style={{ 
                    top: 10,
                    paddingBottom: 0
                }}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Lottie 
                        animationData={successAnimation} 
                        loop={false} 
                        autoplay 
                        style={{ width: '200px', height: '200px', margin: '0 auto' }} 
                    />
                    <Title level={3} style={{ marginTop: '20px', marginBottom: '10px' }}>
                        Gratulacje! 🎉
                    </Title>
                    <Paragraph style={{ fontSize: '16px' }}>
                        Wszystkie dokumenty zostały pomyślnie podpisane!
                    </Paragraph>
                    <Paragraph type="secondary">
                        Możesz teraz w pełni korzystać z aplikacji.
                    </Paragraph>
                    <Button 
                        type="primary" 
                        size="large"
                        onClick={() => {
                            setIsSuccessModalVisible(false);
                            onClose();
                        }}
                        style={{ marginTop: '20px' }}
                    >
                        Rozpocznij pracę
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default DocumentSigning;